import { useEffect, useRef, useState } from "react";

import { Icon } from "./Icon";
import { api, type PickablePerson } from "../services/api";

/**
 * Searchable people combobox for form `user_picker` fields. Queries
 * /api/forms/people (live Entra via CIPP when configured, else portal users in
 * the caller's company) and stores the selected person's value (email/UPN).
 */
export function PeoplePicker({
  id,
  value,
  onChange,
  disabled,
  placeholder,
  companyId,
}: {
  id: string;
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
  placeholder?: string;
  /**
   * iQuest staff only: scope the lookup to a client they're filing on behalf
   * of (or previewing for). Ignored server-side for non-staff callers.
   */
  companyId?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PickablePerson[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(value);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Debounced search whenever the menu is open and the query changes.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const rows = await api.forms.people(
          query.trim() || undefined,
          companyId ?? undefined,
        );
        if (!cancelled) setResults(rows);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, query, companyId]);

  function choose(person: PickablePerson) {
    onChange(person.value);
    setSelectedLabel(person.label);
    setOpen(false);
    setQuery("");
  }

  function clear() {
    onChange(null);
    setSelectedLabel(null);
    setQuery("");
  }

  return (
    <div className="iq-combo" ref={wrapRef}>
      <div className="iq-combo-field">
        <input
          id={id}
          className="iq-input"
          type="text"
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder ?? "Search for a person…"}
          value={open ? query : (selectedLabel ?? value ?? "")}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
        />
        {value && !open && (
          <button type="button" className="iq-combo-clear" aria-label="Clear" onClick={clear}>
            <Icon name="x" size={14} />
          </button>
        )}
      </div>

      {open && (
        <div className="iq-combo-menu" role="listbox">
          {loading && <div className="iq-combo-empty">Searching…</div>}
          {!loading && results.length === 0 && (
            <div className="iq-combo-empty">No people found.</div>
          )}
          {!loading &&
            results.map((p) => (
              <button
                type="button"
                key={`${p.source}:${p.value}`}
                className="iq-combo-option"
                onClick={() => choose(p)}
              >
                <span className="iq-combo-label">{p.label}</span>
                {p.sublabel && <span className="iq-combo-sub">{p.sublabel}</span>}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

export default PeoplePicker;
